import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { Section, WizardNew as Wizard } from "../../../../../../components/wizard";
import ScheduleTypes from "enums/scheduleTypes";
import { RolePermissions, RouteNames } from "enums";
import { useEffect, useMemo, useState } from "react";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import { useNavigate, useParams } from "react-router";
import Restricted from "../../../../../Application/Restricted";
import AccessRestrictedMessage from "../../../../../../components/restrictedRoute/AccessRestrictedMessage";
import People from "./People";
import { campaignsStateSelector } from "../../state/selectors";
import moment from "moment";
import dateTimeUtils from "utils/dateTimeUtils";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type CreateCampaignRunPayload, type ScheduleConfiguration } from "../../types/models";
import { Schedule } from "./Schedule";
import { Confirmation } from "./Confirmation";
import { EmailSendSessionPeopleTypes } from "enums/emailSendSessionPeopleTypes";
import { bindAction } from "interfaces";
import { createCampaignSend } from "../../state/thunks/campaignSendThunk";
import { bindActionCreators } from "redux";
import userPermissionActions from "features/Library/PeopleAssignments/state/actions/userPermissionActions";
import groupPermissionActions from "features/Library/PeopleAssignments/state/actions/groupPermissionActions";
import Sections from "enums/sections";
import { resetSendCreation } from "../../state/slices/campaignSendSlice";
import { reset as resetSelectedGroupTemplates } from "features/Library/PeopleAssignments/state/slice/TemplateOverviewSlice";
import { resetAppliedFilter as resetGroupTemplatesFilter } from "features/Library/PeopleAssignments/state/slice/TemplateFiltersSlice";
import { PreventTransitionPrompt } from "components";
import { getCampaign } from "../../state/thunks/campaignDetailsThunk";
import { clearCampaign } from "../../state/slices/campaignDetailsSlice";
import { shouldShowModalForCampaigns } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";
import LicenseConfirmationContainer from "./containers/LicenseConfirmationContainer";

export type CreateSendAll = PropsFromRedux;

enum CreateSendSteps {
  "People",
  "Schedule",
  "Confirmation",
}
const SEND_SESSION_CREATION_EXIT_MODAL_TITLE = "Exit Without Saving?";
const SEND_SESSION_CREATION_EXIT_MODAL_MESSAGE =
  "You have unsaved changes. Are you sure you want to exit? All information entered will be lost.";

const getScheduleConfigurationDefaults = (
  communicationTimeZoneFeature: boolean,
  timeZone?: string,
): ScheduleConfiguration => ({
  type: ScheduleTypes.SendNow,
  date: dateTimeUtils.formatCurrentDate("MM/DD/YYYY"),
  time: dateTimeUtils.dateToTimeString(dateTimeUtils.roundTime(new Date(), 5), "hh:mm A"),
  timeZone: communicationTimeZoneFeature ? timeZone ?? "UTC" : null,
});

const CreateSend = (props: CreateSendAll) => {
  const {
    selectedUserIds,
    selectedGroupIds,
    selectedTemplateIds,
    timeZone,
    campaignTitle,
    created,
    isLoading,
    createSend,
    cleanModalState,
    getCampaign,
    checkShouldShowLicensingModal,
    flowId,
  } = props;

  const [modalVisible, showModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    getCampaign(Number(params.id));
    return () => {
      cleanModalState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const params = useParams();
  const [wizardPages, { onNext, onPrevious, onFinish }] = useWizardStepsManager([
    CreateSendSteps.People,
    CreateSendSteps.Schedule,
    CreateSendSteps.Confirmation,
  ]);

  const openLicenseModal = async () => {
    if (isProcessing || isLoading) {
      return;
    }

    setIsProcessing(true);
    try {
      const shouldShowModal = await checkShouldShowLicensingModal({
        campaignId: params?.id || "",
        userIds: selectedUserIds,
        groupIds: selectedGroupIds,
        flowId: flowId?.toString(),
      });

      if (shouldShowModal) {
        showModal(true);
      } else {
        createCampaignSend();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const communicationTimeZoneFeature = useFeatureFlag(FeatureFlags.CommunicationTimezone);

  const renderHeaderStub = () => <></>;

  const scheduleConfigurationDefaults = useMemo(
    () => getScheduleConfigurationDefaults(communicationTimeZoneFeature, timeZone),
    [communicationTimeZoneFeature, timeZone],
  );

  const [scheduleConfiguration, setScheduleConfiguration] = useState(scheduleConfigurationDefaults);

  useEffect(() => {
    setScheduleConfiguration(scheduleConfigurationDefaults);
  }, [scheduleConfigurationDefaults]);

  const scheduledAsDate = useMemo(() => {
    const selectedDateTimeString = moment(`${scheduleConfiguration.date} ${scheduleConfiguration.time}`).format(
      "YYYY-MM-DD HH:mm:ss",
    );
    if (scheduleConfiguration.timeZone && scheduleConfiguration.type !== ScheduleTypes.SendNow) {
      return moment.tz(selectedDateTimeString, scheduleConfiguration.timeZone).toDate();
    }

    return new Date(`${scheduleConfiguration.date} ${scheduleConfiguration.time}`);
  }, [scheduleConfiguration]);

  const isDateValid = scheduleConfiguration.type === ScheduleTypes.SendNow || scheduledAsDate > new Date();

  const goToStartPage = (navigate: ReturnType<typeof useNavigate>, campaignId: string | undefined) => {
    navigate(`/${RouteNames.contentSimulatedPhishingCampaigns}/${campaignId}/sends`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (created) {
      goToStartPage(navigate, params.id);
    }
  }, [created, navigate, params.id]);

  const onCancel = () => goToStartPage(navigate, params.id);

  const getPeopleIds = (): {
    id: number;
    type: EmailSendSessionPeopleTypes;
  }[] => {
    return selectedGroupIds
      .map((x) => ({ id: x, type: EmailSendSessionPeopleTypes.group }))
      .concat(selectedUserIds.map((x) => ({ id: x, type: EmailSendSessionPeopleTypes.user })))
      .concat(selectedTemplateIds.map((x) => ({ id: x, type: EmailSendSessionPeopleTypes.templateGroup })));
  };

  const createCampaignSend = (packIds?: number[]) => {
    const payload: CreateCampaignRunPayload = {
      runNow: scheduleConfiguration.type === ScheduleTypes.SendNow,
      isTest: false,
      peopleIds: getPeopleIds(),
      startDateTime: scheduledAsDate.toISOString(),
      timeZone: scheduleConfiguration.type === ScheduleTypes.SendOn ? scheduleConfiguration.timeZone : null,
      packIds,
    };

    createSend(Number.parseInt(params.id!, 10), payload);
  };

  const promptText = useMemo(() => {
    return scheduleConfiguration.type === ScheduleTypes.SendNow ? (
      <>
        By clicking <b>Send Now</b>, all emails will send immediately. This action cannot be undone.
      </>
    ) : (
      <>
        Clicking <b>Schedule</b> below will set emails to send on the scheduled date and time and cannot be undone.
      </>
    );
  }, [scheduleConfiguration.type]);

  const isButtonDisabled = isLoading || isProcessing;

  return (
    <Restricted
      permissions={[RolePermissions.UsersView]}
      renderContent={(hasPermissions) => (
        <>
          <Wizard
            title="Create Send"
            finishButtonLabel={scheduleConfiguration.type === ScheduleTypes.SendNow ? "Send Now" : "Schedule"}
            onCancel={onCancel}
            onFinish={onFinish}
            onProgress={onNext}
            onRegress={onPrevious}
            isSaveInProgress={isButtonDisabled}
            isFinishButtonDisabled={isButtonDisabled}
            renderCustomHeader={renderHeaderStub}
          >
            <Section
              label="People"
              className="no-padding"
              isLocked={
                !hasPermissions ||
                (selectedUserIds.length === 0 && selectedGroupIds.length === 0 && selectedTemplateIds.length === 0)
              }
              required
            >
              <Restricted
                permissions={[RolePermissions.UsersView]}
                placeholder={<AccessRestrictedMessage />}
                permissionPredicate={() => hasPermissions}
              >
                <People />
              </Restricted>
            </Section>
            <Section label="Schedule" className="scrollable-content" isLocked={!hasPermissions} required>
              <Schedule
                useTimeZone={communicationTimeZoneFeature}
                configuration={scheduleConfiguration}
                onScheduleChange={setScheduleConfiguration}
              />
              {!isDateValid && <div>Specify date in the future.</div>}
            </Section>
            <Section label="Confirmation" className="scrollable-content" isLocked={!hasPermissions}>
              <Confirmation
                schedule={
                  scheduleConfiguration.type === ScheduleTypes.SendNow
                    ? dateTimeUtils.getCurrentDate()
                    : scheduledAsDate
                }
                scheduleTimeZone={
                  scheduleConfiguration.type === ScheduleTypes.SendNow ? null : scheduleConfiguration.timeZone
                }
                communicationTitle={campaignTitle}
                selectedGroupIds={selectedGroupIds}
                selectedUserIds={selectedUserIds}
                selectedTemplateIds={selectedTemplateIds}
                onConfirm={openLicenseModal}
                promptText={promptText}
                acceptHandlers={wizardPages[CreateSendSteps.Confirmation]}
              />
            </Section>
          </Wizard>
          <PreventTransitionPrompt
            when={!created}
            title={SEND_SESSION_CREATION_EXIT_MODAL_TITLE}
            message={SEND_SESSION_CREATION_EXIT_MODAL_MESSAGE}
          />

          <LicenseConfirmationContainer
            campaignId={params.id}
            modalVisible={modalVisible}
            showModal={showModal}
            setIsProcessing={setIsProcessing}
            createCampaignSend={createCampaignSend}
          />
        </>
      )}
    />
  );
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  createSend: bindAction(createCampaignSend, dispatch),
  getCampaign: bindAction(getCampaign, dispatch),
  cleanModalState: () => {
    dispatch(clearCampaign());
    bindActionCreators(userPermissionActions(Sections.tdf), dispatch).resetUsersAssignment();
    bindActionCreators(groupPermissionActions(Sections.tdf), dispatch).resetGroupsAssignment();
    dispatch(resetSelectedGroupTemplates());
    dispatch(resetGroupTemplatesFilter());
    dispatch(resetSendCreation());
  },
  checkShouldShowLicensingModal: bindAction(shouldShowModalForCampaigns, dispatch),
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    created: base.sends.create.created,
    isLoading: base.sends.create.isLoading,
    campaignTitle: base.details.campaign.name,
    selectedUserIds: base.sends.people.assignedUsers.userIds,
    selectedGroupIds: base.sends.people.assignedGroups.groupIds,
    selectedTemplateIds: state.library.groupTemplatesAssignment.templatesToBeAssigned.selected,
    timeZone: state.userProfile.timeZone,
    flowId: base.details.campaign.remedialTraining.flowId,
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateSend);
export default ConnectedComponent;
